<template>
  <v-data-table
    :headers="headers"
    :items="categories"
    :sort-by="[{ key: '#', order: 'asc' }]"
  >
    <!-- Top bar: search + New Category -->
    <template v-slot:top>
      <v-toolbar flat class="bg-white px-4 py-3">
        <v-row align="center" justify="space-between" no-gutters>
          <!-- Search -->
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="search"
              density="comfortable"
              variant="outlined"
              hide-details
              append-inner-icon="mdi-magnify"
              label="Search categories..."
              @input="initialize"
            />
          </v-col>

          <!-- New Category button -->
          <v-col cols="12" sm="3" class="text-right">
            <v-btn
              variant="elevated"
              color="blue"
              size="large"
              prepend-icon="mdi-plus-thick"
              class="rounded-pill px-6 font-semibold text-white"
              @click="dialog = true"
            >
              New Category
            </v-btn>
          </v-col>
        </v-row>
      </v-toolbar>
    </template>

    <!-- Row numbering -->
    <template v-slot:[`item.id`]="{ index }">
      {{ index + 1 }}
    </template>

    <!-- Description fallback -->
    <template v-slot:[`item.description`]="{ item }">
      {{ item.description || 'N/A' }}
    </template>

    <!-- Actions -->
    <template v-slot:[`item.actions`]="{ item }">
      <v-btn icon size="small" variant="flat" color="primary" @click="editItem(item)">
        <v-icon>mdi-pencil</v-icon>
      </v-btn>
      <v-btn icon size="small" variant="flat" color="error" @click="deleteItem(item)">
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </template>

    <!-- No-data slot -->
    <template #no-data>
      <v-card flat class="text-center pa-4">
        <v-icon size="36" color="grey">mdi-database-remove</v-icon>
        <div class="text-subtitle-2 mt-2">No categories found</div>
        <v-btn color="black" class="mt-2" @click="initialize">
          Reset
        </v-btn>
      </v-card>
    </template>
  </v-data-table>

  <!-- Add/Edit Dialog -->
  <v-dialog v-model="dialog" max-width="500px">
    <v-card>
      <v-card-title class="text-h6"> {{ formTitle }} </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="isValid" @submit.prevent="save">
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="editedItem.title"
                label="Name"
                :rules="rules.name"
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editedItem.slug"
                label="Slug"
                :rules="rules.slug"
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="editedItem.description"
                label="Description"
                dense
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" color="grey" @click="close">Cancel</v-btn>
        <v-btn
          variant="elevated"
          color="black"
          class="rounded-pill px-6 font-semibold text-white"
          :disabled="!isValid"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Delete Confirmation -->
  <v-dialog v-model="dialogDelete" max-width="400px">
    <v-card>
      <v-card-title class="text-h6 text-center">Confirm Deletion</v-card-title>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" color="grey" @click="closeDelete">Cancel</v-btn>
        <v-btn
          variant="elevated"
          color="error"
          class="rounded-pill px-6 font-semibold text-white"
          @click="deleteItemConfirm"
        >
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CategoriesService from "../services/category.services.js";
export default {
  data: () => ({
    dialog: false,
    dialogDelete: false,
    isValid: false,
    rules: {
      name: [
        (value) => {
          if (value?.length >= 3) return true;
          return "You must enter a name.";
        },
      ],
      slug: [
        (value) => {
          if (value?.length >= 3) return true;
          return "You must enter a slug.";
        },
      ],
    },
    headers: [
      {
        title: "#",
        value: "id",
        align: "start",
        sortable: false,
        key: "id",
      },
      { title: "Name", value: "title", key: "title" },
      { title: "Slug", value: "slug", key: "slug" },
      // { title: "Description", value: "description", key: "description" },
      { title: "Actions", key: "actions", sortable: false },
    ],

    editedIndex: -1,
    categories: [],
    editedItem: {
      id: "",
      name: "",
      title:"",
      slug: "",
      description: "",
    },
    defaultItem: {
      id: "",
      name: "",
      title:"",
      slug: "",
      description: "",
    },
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Category" : "Edit Category";
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
  },

  created() {
    this.getCategories();
  },

  methods: {
    async validate() {
      this.isValid = await this.$refs.form.validate();
    },

    handleLogout() {
      this.$store.commit("logout");
      this.$store.dispatch("resetInactivityTimeout");
      this.$store.dispatch("auth/logout");
      this.$store.dispatch("resetInactivityTimeout");
      this.$router.push("/login");
      this.$store.dispatch("alert/success", "Logout Successfully");
    },
    
    getCategories() {
      return CategoriesService.index().then(
        (response) => {
          if (response.status == 200) {
            this.categories = response.data.data;
            this.loading = false;
          } else {
            this.categories = [];
            this.loading = false;
            console.log(response.data.console.error);
          }
        },
        (error) => {
          this.categories = [];
          if (error.response.status == 401) {
            this.handleLogout();
          }
          console.log(error);
          this.$store.dispatch("alert/error", error.message);
        }
      );
    },

    editItem(item) {
      this.editedIndex = this.categories.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    deleteItem(item) {
      this.editedIndex = this.categories.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      CategoriesService.delete(this.editedItem).then(
        (response) => {
          if (response.status == 200) {
            this.$store.dispatch("alert/success", response.data.message);
            this.categories.splice(this.editedIndex, 1);
            this.closeDelete();
          } else {
            this.$store.dispatch("alert/error", response.data.message);
          }
        },
        (error) => {
          if (error.response.status == 422) {
            this.$store.dispatch("alert/error", error.response.data.message);
          }
          console.log(error);
          this.$store.dispatch("alert/error", error.response.data.message);
        }
      );
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    async save() {
  this.editedItem.name = this.editedItem.title;
  const valid = await this.$refs.form.validate();
  if (valid) {
    if (this.editedIndex > -1) {
      // Update
      CategoriesService.update(this.editedItem).then((response) => {
        if (response.status == 200) {
          this.categories[this.editedIndex] = { ...this.editedItem };
          this.$store.dispatch("alert/success", response.data.message);
          this.close();
        } else {
          this.$store.dispatch("alert/error", response.data.message);
        }
      }).catch((error) => {
        this.handleError(error);
      });
    } else {
      // Create
      CategoriesService.store(this.editedItem).then((response) => {
        if (response.status == 201) {
          this.categories.push(response.data.data); // Use returned category
          this.$store.dispatch("alert/success", response.data.message);
          this.close();
        } else {
          this.$store.dispatch("alert/error", response.data.message);
        }
      }).catch((error) => {
        this.handleError(error);
      });
    }
  }
}

  },
};
</script>